import React from 'react';
import Icon from '../../../components/AppIcon';


const InfrastructureSection = ({ commune }) => {
  if (!commune) return null;

  const infrastructureCategories = [
  {
    id: 'transport',
    title: 'Transport',
    icon: 'Car',
    color: 'bg-blue-50 text-blue-600',
    items: commune?.infrastructure?.transport
  },
  {
    id: 'education',
    title: 'Éducation',
    icon: 'GraduationCap',
    color: 'bg-green-50 text-green-600',
    items: commune?.infrastructure?.education
  },
  {
    id: 'healthcare',
    title: 'Santé',
    icon: 'Heart',
    color: 'bg-red-50 text-red-600',
    items: commune?.infrastructure?.healthcare
  },
  {
    id: 'utilities',
    title: 'Services Publics',
    icon: 'Zap',
    color: 'bg-yellow-50 text-yellow-600',
    items: commune?.infrastructure?.utilities
  }];


  return (
    <div className="bg-surface">
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-16">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Icon name="Building2" size={20} className="text-brand-gold" />
            <span className="text-sm font-medium text-brand-gold uppercase tracking-wide">
              Infrastructure & Services
            </span>
          </div>
          
          <h2 className="text-3xl lg:text-4xl font-heading font-bold text-text-primary mb-4">
            Équipements & Commodités
          </h2>
          
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Découvrez les infrastructures modernes et les services essentiels qui facilitent la vie quotidienne
          </p>
        </div>

        {/* Infrastructure Categories */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {infrastructureCategories?.map((category) =>
          <div key={category?.id} className="bg-white rounded-xl p-6 shadow-warm hover:shadow-deep transition-shadow duration-300">
              <div className={`inline-flex p-3 rounded-lg ${category?.color} mb-4`}>
                <Icon name={category?.icon} size={24} />
              </div>
              
              <h3 className="font-heading font-semibold text-text-primary mb-4">
                {category?.title}
              </h3>
              
              <div className="space-y-3">
                {category?.items?.map((item, index) =>
              <div key={index} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-1">
                      <div className="w-2 h-2 bg-brand-gold rounded-full" />
                    </div>
                    <div>
                      <p className="font-medium text-text-primary text-sm">{item?.name}</p>
                      <p className="text-xs text-text-secondary">{item?.distance}</p>
                      {item?.rating &&
                  <div className="flex items-center space-x-1 mt-1">
                          <Icon name="Star" size={12} className="text-yellow-500" />
                          <span className="text-xs text-text-secondary">{item?.rating}/5</span>
                        </div>
                  }
                    </div>
                  </div>
              )}
              </div>
            </div>
          )}
        </div>

        {/* Development Projects */}
        <div className="bg-white rounded-2xl p-8 shadow-warm mb-12 border-none">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-2xl font-heading font-bold text-text-primary mb-2">
                Projets de Développement
              </h3>
              <p className="text-text-secondary">
                Investissements futurs qui valoriseront la commune
              </p>
            </div>
            
            <div className="flex items-center space-x-2 text-sm text-brand-gold">
              <Icon name="Calendar" size={16} />
              <span>Mise à jour: Août 2025</span>
            </div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {commune?.developmentProjects?.map((project, index) =>
            <div key={index} className="border-border rounded-xl p-6 hover:shadow-warm transition-shadow duration-300 border-none border-0">
                <div className="flex items-center justify-between mb-4">
                  <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                project?.status === 'En cours' ? 'bg-blue-100 text-blue-600' :
                project?.status === 'Planifié' ? 'bg-yellow-100 text-yellow-600' : 'bg-green-100 text-green-600'}`
                }>
                    {project?.status}
                  </span>
                  <Icon name="Building" size={16} className="text-text-secondary" />
                </div>
                
                <h4 className="font-semibold text-text-primary mb-2">
                  {project?.name}
                </h4>
                
                <p className="text-sm text-text-secondary mb-4">
                  {project?.description}
                </p>
                
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-secondary">Budget:</span>
                    <span className="font-medium text-text-primary">{project?.budget}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-text-secondary">Achèvement:</span>
                    <span className="font-medium text-text-primary">{project?.completion}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Transportation Map */}
        <div className="bg-white rounded-2xl p-8 shadow-warm">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-2xl font-heading font-bold text-text-primary mb-2">
                Accessibilité & Transport
              </h3>
              <p className="text-text-secondary">
                Connexions vers les principaux centres d'affaires
              </p>
            </div>
            
            <button className="flex items-center space-x-2 px-4 py-2 bg-brand-gold text-white rounded-lg hover:bg-brand-earth transition-colors duration-300">
              <Icon name="Navigation" size={16} />
              <span className="text-sm font-medium">Itinéraires</span>
            </button>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Commute Times */}
            <div>
              <h4 className="font-semibold text-text-primary mb-4">
                Temps de Trajet
              </h4>
              
              <div className="space-y-4">
                {commune?.commuteData?.map((destination, index) =>
                <div key={index} className="flex items-center justify-between p-4 bg-surface rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Icon name="MapPin" size={16} className="text-brand-gold" />
                      <div>
                        <p className="font-medium text-text-primary">{destination?.name}</p>
                        <p className="text-sm text-text-secondary">{destination?.type}</p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="font-semibold text-text-primary">{destination?.time}</p>
                      <p className="text-xs text-text-secondary">{destination?.mode}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Map Placeholder */}
            <div className="relative h-80 bg-surface rounded-xl overflow-hidden">
              <iframe
                width="100%"
                height="100%"
                loading="lazy"
                title={`Carte de ${commune?.name}`}
                referrerPolicy="no-referrer-when-downgrade"
                src={`https://www.google.com/maps?q=${commune?.coordinates?.lat},${commune?.coordinates?.lng}&z=14&output=embed`}
                className="border-0" />

              
              <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg">
                <p className="text-sm font-medium text-text-primary">{commune?.name}</p>
                <p className="text-xs text-text-secondary">Centre de la commune</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>);

};

export default InfrastructureSection;